import type { Rect, Stroke } from './stroke';
import { rectsIntersect } from './stroke';
import { buildRibbon, fillRibbon } from './ribbon';
import { drawTextBox, textBoxRect, textBoxFont } from './text';
import { resolveInk, isHighlight, type Theme } from '../theme/tokens';
import { drawTemplate } from '../theme/templates';
import type { Page, TextBox, Image } from '../store/types';

export const TILE_SIZE = 512; // world units per tile at scale 1

export interface TileCoord { col: number; row: number }

export function tilesInRect(world: Rect): TileCoord[] {
  const c0 = Math.floor(world.x / TILE_SIZE), c1 = Math.floor((world.x + world.w) / TILE_SIZE);
  const r0 = Math.floor(world.y / TILE_SIZE), r1 = Math.floor((world.y + world.h) / TILE_SIZE);
  const out: TileCoord[] = [];
  for (let row = r0; row <= r1; row++) for (let col = c0; col <= c1; col++) out.push({ col, row });
  return out;
}

export function tileWorldRect(t: TileCoord): Rect {
  return { x: t.col * TILE_SIZE, y: t.row * TILE_SIZE, w: TILE_SIZE, h: TILE_SIZE };
}

type Tile = ImageBitmap | HTMLCanvasElement;

/** Canonical iOS canvas memory release: zero the backing store (canvases) or close (bitmaps). */
function releaseTile(v: Tile): void {
  if (v instanceof HTMLCanvasElement) { v.width = 0; v.height = 0; }
  else (v as ImageBitmap).close?.();
}

/** LRU cache with a byte budget (tile backing stores dominate memory, not entry count). */
export class TileCache {
  private map = new Map<string, { tile: Tile; bytes: number }>();
  private totalBytes = 0;
  constructor(
    private maxBytes = 40 * 1024 * 1024,
    private onEvict: (v: Tile, key: string) => void = releaseTile,
  ) {}
  get(key: string): Tile | undefined {
    const e = this.map.get(key);
    if (e === undefined) return undefined;
    this.map.delete(key); this.map.set(key, e); // bump recency
    return e.tile;
  }
  set(key: string, v: Tile, bytes: number) {
    const prev = this.map.get(key);
    if (prev) { this.map.delete(key); this.totalBytes -= prev.bytes; }
    this.map.set(key, { tile: v, bytes });
    this.totalBytes += bytes;
    while (this.totalBytes > this.maxBytes && this.map.size > 1) {
      const oldest = this.map.keys().next().value as string;
      if (oldest === key) break; // never evict the entry just inserted
      const evicted = this.map.get(oldest)!;
      this.map.delete(oldest);
      this.totalBytes -= evicted.bytes;
      this.onEvict(evicted.tile, oldest);
    }
  }
  invalidate(pred: (key: string) => boolean) {
    for (const key of [...this.map.keys()]) {
      if (pred(key)) {
        const e = this.map.get(key)!;
        this.map.delete(key);
        this.totalBytes -= e.bytes;
        this.onEvict(e.tile, key);
      }
    }
  }
  clear() { this.invalidate(() => true); }
}

type BitmapSource = ImageBitmap | HTMLCanvasElement;

/**
 * Byte-budgeted LRU cache of asynchronously-loaded bitmaps (M4: page images
 * and any future async raster). Same eviction idiom as TileCache
 * (canvas-zeroing / bitmap close), but entries arrive via promise-returning
 * loaders: `request` is single-flight per key, and resolution fires `onready`
 * with the world rect the caller wants repainted (the renderer wires this to
 * invalidateRect + onneedsredraw). Failed loads are reported through
 * `onerror` and the in-flight marker is forgotten so the next request
 * retries. Bytes are estimated as w*h*4 (RGBA backing store).
 */
export class AsyncBitmaps {
  private map = new Map<string, { bmp: BitmapSource; bytes: number }>();
  private totalBytes = 0;
  private inflight = new Set<string>();
  /** Fired after a bitmap is stored; rect = world area to repaint. */
  onready: (rect: Rect) => void = () => {};
  /** Fired when a loader rejects (load already forgotten; safe to retry). */
  onerror: (key: string, err: unknown) => void = (key, err) => {
    console.warn(`[cote] bitmap load failed: ${key}`, err);
  };
  constructor(private maxBytes = 64 * 1024 * 1024) {}

  get(key: string): BitmapSource | undefined {
    const e = this.map.get(key);
    if (e === undefined) return undefined;
    this.map.delete(key); this.map.set(key, e); // bump recency
    return e.bmp;
  }

  /** Kick an async load unless the key is cached or already loading. */
  request(key: string, rect: Rect, loader: () => Promise<BitmapSource>): void {
    if (this.map.has(key) || this.inflight.has(key)) return;
    this.inflight.add(key);
    loader().then(
      (bmp) => {
        this.inflight.delete(key);
        this.store(key, bmp);
        this.onready(rect);
      },
      (err) => {
        this.inflight.delete(key);
        this.onerror(key, err);
      },
    );
  }

  private store(key: string, bmp: BitmapSource): void {
    const bytes = Math.max(1, bmp.width * bmp.height * 4);
    const prev = this.map.get(key);
    if (prev) { this.map.delete(key); this.totalBytes -= prev.bytes; releaseTile(prev.bmp); }
    this.map.set(key, { bmp, bytes });
    this.totalBytes += bytes;
    while (this.totalBytes > this.maxBytes && this.map.size > 1) {
      const oldest = this.map.keys().next().value as string;
      if (oldest === key) break; // never evict the entry just inserted
      const evicted = this.map.get(oldest)!;
      this.map.delete(oldest);
      this.totalBytes -= evicted.bytes;
      releaseTile(evicted.bmp);
    }
  }

  clear(): void {
    for (const e of this.map.values()) releaseTile(e.bmp);
    this.map.clear();
    this.totalBytes = 0;
    this.inflight.clear();
  }
}

/**
 * Async page underlay (M4 spec §2.2: PDF page bitmaps). The provider owns its
 * per-bucket raster cache; the renderer only asks "got a bitmap for this
 * bucket?" during tile raster and requests a render when it doesn't. On
 * completion the provider's plumbing (engine) invalidates `rect` and
 * schedules a redraw.
 */
export interface Underlay {
  rect: Rect;                 // world rect the underlay covers (page rect)
  key: string;                // identity, e.g. `pdf:<fileId>:<pageNo>`
  get(bucket: number): CanvasImageSource | undefined;
  request(bucket: number): void;
  /**
   * Optional resource disposal — the renderer calls it when the provider is
   * replaced/cleared (setUnderlay) or torn down (destroy). PDF providers
   * release their raster cache AND the pdfjs document/worker here; without
   * it, paging an N-page PDF leaks N workers.
   */
  destroy?(): void;
}

function imageRect(im: Image): Rect {
  return { x: im.x, y: im.y, w: im.w, h: im.h };
}

/**
 * Zoom bucket = power of 2 raster scale so tiles stay crisp across zoom.
 * Clamped to 2 so tile canvases never exceed 1024px — 2048px canvases blow
 * iOS Safari's canvas memory budget; mild softness above 2x is the tradeoff.
 */
export function zoomBucket(scale: number): number {
  return Math.pow(2, Math.round(Math.log2(Math.min(2, Math.max(0.25, scale)))));
}

const A4 = { w: 794, h: 1123 }; // ~96dpi A4 in world units
export function pageRectFor(page: Page): Rect | undefined {
  if (page.kind !== 'fixed') return undefined;
  const size = page.size ?? A4; // M4: PDF pages carry their own size (pt × 96/72)
  return { x: 0, y: 0, w: size.w, h: size.h };
}

/**
 * Renders committed strokes into cached tiles and composites visible tiles
 * onto the main (static) canvas. Strokes live in world coordinates.
 */
export class TileRenderer {
  private cache = new TileCache(); // 40 MB byte budget
  private strokes: Stroke[] = [];
  private excluded = new Set<string>(); // strokes hidden from tiles (active lasso selection)
  private textBoxes: TextBox[] = [];
  // Best-effort rect cache for text-box invalidation (see setTextBoxes doc).
  private textBoxRects = new Map<string, Rect>();
  private measureCanvasCtx: CanvasRenderingContext2D | null = null;
  private images: Image[] = [];
  private imageLoader: ((fileId: string) => Promise<ImageBitmap | HTMLCanvasElement>) | null = null;
  private underlay: Underlay | null = null;
  /**
   * Shared async bitmap cache for image fills (64 MB). Public so the engine
   * can drop it on teardown and tests can inspect it; keys are `img:<fileId>`.
   */
  readonly bitmaps = new AsyncBitmaps();
  /**
   * Fired when an async bitmap resolves (after its rect's tiles were
   * invalidated) — the engine wires this to its static-redraw scheduler.
   */
  onneedsredraw: () => void = () => {};
  theme: Theme = 'light';

  constructor(private page: Page, private dpr = (globalThis.devicePixelRatio ?? 1)) {
    this.bitmaps.onready = (rect) => {
      this.invalidateRect(rect);
      this.onneedsredraw();
    };
  }

  setStrokes(strokes: Stroke[]) { this.strokes = strokes.slice(); this.cache.clear(); }
  getStrokes(): Stroke[] { return this.strokes; }
  getTextBoxes(): TextBox[] { return this.textBoxes; }

  /**
   * Replace the text box list (copy) and invalidate tiles touching the
   * old⊕new footprint of any added/removed/changed box (identity: same id +
   * every field that affects layout or appearance).
   *
   * Rect accuracy note: computing a box's rect needs a real canvas context
   * with `ctx.font` set (wrapping depends on measured glyph widths) — tile
   * contexts only exist lazily, per visible tile, at draw() time, so there
   * is no tile ctx to reuse here. PRAGMATIC fix: keep one small persistent
   * scratch canvas purely for measurement. `measureText` is unaffected by a
   * context's current scale/translate transform, so the scratch ctx's widths
   * match what any tile's (DPR/zoom-bucket-scaled) ctx would report — same
   * world-space numbers. Each tile draw (see drawTextPass) also refreshes
   * `textBoxRects` with the rect it actually used, so the cache self-heals
   * from real renders too. In jsdom tests the canvas stub always reports
   * width 0 (tests/setup.ts), so this degrades to a coarse single-line-height
   * rect there — fine, since unit tests don't assert pixel-perfect
   * invalidation; real browsers get accurate rects.
   */
  setTextBoxes(boxes: TextBox[]) {
    const next = boxes.slice();
    const touched: Rect[] = [];
    const nextIds = new Set(next.map((b) => b.id));
    for (const old of this.textBoxes) {
      if (nextIds.has(old.id)) continue; // still present; handled below
      const r = this.textBoxRects.get(old.id);
      if (r) touched.push(r);
      this.textBoxRects.delete(old.id);
    }
    const prevById = new Map(this.textBoxes.map((b) => [b.id, b] as const));
    for (const b of next) {
      const prev = prevById.get(b.id);
      if (prev && sameTextBox(prev, b)) continue; // unchanged: leave its cached rect alone
      const prevRect = this.textBoxRects.get(b.id);
      if (prevRect) touched.push(prevRect);
      const rect = this.measureTextBoxRect(b);
      this.textBoxRects.set(b.id, rect);
      touched.push(rect);
    }
    this.textBoxes = next;
    for (const r of touched) this.invalidateRect(r);
  }

  private measureTextBoxRect(box: TextBox): Rect {
    if (!this.measureCanvasCtx) {
      const c = document.createElement('canvas');
      c.width = 1; c.height = 1;
      this.measureCanvasCtx = c.getContext('2d');
    }
    const ctx = this.measureCanvasCtx;
    if (!ctx) return { x: box.x, y: box.y, w: box.w, h: box.fontSize * 1.4 };
    ctx.font = textBoxFont(box);
    return textBoxRect(box, (s) => ctx.measureText(s).width);
  }
  /**
   * Replace the image list (copy) and invalidate tiles touching the old⊕new
   * rect of any added/removed/changed image. Unlike text boxes, image rects
   * are explicit x/y/w/h — no measurement needed.
   */
  setImages(images: Image[]) {
    const next = images.slice();
    const touched: Rect[] = [];
    const nextIds = new Set(next.map((i) => i.id));
    for (const old of this.images) {
      if (!nextIds.has(old.id)) touched.push(imageRect(old));
    }
    const prevById = new Map(this.images.map((i) => [i.id, i] as const));
    for (const im of next) {
      const prev = prevById.get(im.id);
      if (prev && sameImage(prev, im)) continue;
      if (prev) touched.push(imageRect(prev));
      touched.push(imageRect(im));
    }
    this.images = next;
    for (const r of touched) this.invalidateRect(r);
  }
  getImages(): Image[] { return this.images; }

  /**
   * Loader the engine registers for image bitmaps (file bytes →
   * createImageBitmap); the renderer requests through AsyncBitmaps during
   * tile raster whenever an image's bitmap isn't cached yet.
   */
  setImageLoader(fn: (fileId: string) => Promise<ImageBitmap | HTMLCanvasElement>) {
    this.imageLoader = fn;
  }

  /**
   * Kick the async bitmap load for one image placement. The engine's
   * image-selection overlay draws OUTSIDE the tile pass (a selected image is
   * excluded from tiles via setImages), so drawImagePass never requests its
   * bitmap — without this entry point a freshly-inserted image would stay
   * invisible until the selection commits. Completion flows through the
   * usual onready → invalidate + onneedsredraw pipeline.
   */
  requestImageBitmap(im: Image): void {
    const loader = this.imageLoader;
    if (!loader || this.bitmaps.get('img:' + im.fileId)) return;
    const fileId = im.fileId;
    this.bitmaps.request('img:' + fileId, imageRect(im), () => loader(fileId));
  }

  /**
   * Set/replace/clear the async page underlay (PDF pages). Invalidates the
   * old and new underlay rects so covered tiles re-raster, and DISPOSES the
   * replaced provider (raster cache + pdfjs document/worker).
   */
  setUnderlay(u: Underlay | null) {
    if (u === this.underlay) return;
    const old = this.underlay;
    this.underlay = u;
    if (old) {
      old.destroy?.();
      this.invalidateRect(old.rect);
    }
    if (u) this.invalidateRect(u.rect);
  }

  addStroke(s: Stroke) { this.strokes.push(s); this.invalidateRect(s.bbox); }
  removeStroke(id: string) {
    const s = this.strokes.find((x) => x.id === id);
    this.strokes = this.strokes.filter((x) => x.id !== id);
    if (s) this.invalidateRect(s.bbox);
  }
  /**
   * Hide the given strokes from tile rasterization (they are drawn transformed
   * on the live layer while a selection is active). Only tiles intersecting
   * strokes whose excluded state actually changed (old ⊕ new) are invalidated.
   */
  setExcluded(ids: Set<string>) {
    const changed = this.strokes.filter((s) => this.excluded.has(s.id) !== ids.has(s.id));
    this.excluded = new Set(ids);
    for (const s of changed) this.invalidateRect(s.bbox);
  }
  setTheme(theme: Theme) { if (theme !== this.theme) { this.theme = theme; this.cache.clear(); } }
  /** Refresh device pixel ratio (e.g. after a window move between monitors). */
  setDpr(dpr: number) { if (dpr !== this.dpr) { this.dpr = dpr; this.cache.clear(); } }

  invalidateRect(world: Rect) {
    const hit = new Set(tilesInRect(world).map((t) => `${t.col}:${t.row}`));
    this.cache.invalidate((key) => hit.has(key.split('@')[0]!));
  }

  /** Composite visible tiles for viewport {x,y,scale} onto ctx (canvas pixels). */
  draw(ctx: CanvasRenderingContext2D, viewport: { x: number; y: number; scale: number }, cssW: number, cssH: number) {
    const { x, y, scale } = viewport;
    const bucket = zoomBucket(scale * this.dpr);
    const worldView: Rect = { x, y, w: cssW / scale, h: cssH / scale };
    ctx.clearRect(0, 0, cssW * this.dpr, cssH * this.dpr);
    const pageRect = pageRectFor(this.page);
    for (const t of tilesInRect(worldView)) {
      if (pageRect && !rectsIntersect(tileWorldRect(t), pageRect)) continue;
      const tile = this.getOrRenderTile(t, bucket);
      const wr = tileWorldRect(t);
      // Snap destination rects to integers so neighboring tiles share exact
      // pixel edges (no hairline seams from fractional destination coords).
      const s = scale * this.dpr;
      const left = Math.round((wr.x - x) * s);
      const top = Math.round((wr.y - y) * s);
      const right = Math.round((wr.x + TILE_SIZE - x) * s);
      const bottom = Math.round((wr.y + TILE_SIZE - y) * s);
      ctx.drawImage(tile as CanvasImageSource, left, top, right - left, bottom - top);
    }
    if (pageRect) this.drawPageBorder(ctx, pageRect, viewport);
  }

  private drawPageBorder(ctx: CanvasRenderingContext2D, pageRect: Rect, vp: { x: number; y: number; scale: number }) {
    ctx.save();
    ctx.strokeStyle = this.theme === 'dark' ? 'rgba(236,234,228,0.15)' : 'rgba(43,43,51,0.15)';
    ctx.lineWidth = 1;
    const s = vp.scale * this.dpr;
    ctx.strokeRect((pageRect.x - vp.x) * s, (pageRect.y - vp.y) * s, pageRect.w * s, pageRect.h * s);
    ctx.restore();
  }

  private getOrRenderTile(t: TileCoord, bucket: number): ImageBitmap | HTMLCanvasElement {
    const key = `${t.col}:${t.row}@${bucket}:${this.theme}`;
    const hit = this.cache.get(key);
    if (hit) return hit;
    const px = TILE_SIZE * bucket;
    const canvas = this.makeCanvas(px, px);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // iOS Safari returns null past its canvas memory ceiling. Release the
      // allocation and hand back a 1x1 placeholder WITHOUT caching it, so the
      // next frame retries instead of pinning a dead tile in the cache.
      canvas.width = 0; canvas.height = 0;
      return this.makeCanvas(1, 1);
    }
    const wr = tileWorldRect(t);
    ctx.scale(bucket, bucket);
    ctx.translate(-wr.x, -wr.y);
    const pageRect = pageRectFor(this.page);
    if (pageRect) { // clip template+ink to page bounds on fixed pages
      ctx.beginPath();
      ctx.rect(pageRect.x, pageRect.y, pageRect.w, pageRect.h);
      ctx.clip();
    }
    // Pass order: underlay (PDF page bitmap, beneath everything) -> template
    // -> images (under all ink/text) -> highlighter (behind ink) -> text ->
    // pen (ink annotates over text; highlighter stays under everything).
    this.drawUnderlayPass(ctx, wr, bucket);
    drawTemplate(ctx, this.page.templateId, this.theme, wr, bucket, pageRect ?? wr);
    this.drawImagePass(ctx, wr);
    this.drawStrokePass(ctx, wr, 'highlighter');
    this.drawTextPass(ctx, wr);
    this.drawStrokePass(ctx, wr, 'pen');
    this.cache.set(key, canvas, px * px * 4); // RGBA backing-store estimate
    return canvas;
  }

  /**
   * Draw the page underlay (PDF bitmap) if the provider has a raster for
   * this bucket; otherwise kick the async render — its completion path
   * invalidates the underlay rect and schedules a redraw (engine wiring).
   * The bitmap is scaled to cover the underlay's world rect.
   */
  private drawUnderlayPass(ctx: CanvasRenderingContext2D, wr: Rect, bucket: number) {
    const u = this.underlay;
    if (!u || !rectsIntersect(u.rect, wr)) return;
    const src = u.get(bucket);
    if (src) ctx.drawImage(src, u.rect.x, u.rect.y, u.rect.w, u.rect.h);
    else u.request(bucket);
  }

  /**
   * Draw ready image bitmaps into their world rects; request missing ones
   * through the engine-registered loader (single-flight per fileId inside
   * AsyncBitmaps). The request rect is the union of every active placement
   * of that file so all copies repaint when the bitmap lands.
   */
  private drawImagePass(ctx: CanvasRenderingContext2D, wr: Rect) {
    for (const im of this.images) {
      if (im.deletedAt) continue;
      const r = imageRect(im);
      if (!rectsIntersect(r, wr)) continue;
      const bmp = this.bitmaps.get('img:' + im.fileId);
      if (bmp) { ctx.drawImage(bmp, r.x, r.y, r.w, r.h); continue; }
      const loader = this.imageLoader;
      if (!loader) continue;
      let union = r;
      for (const other of this.images) {
        if (other.deletedAt || other.fileId !== im.fileId || other.id === im.id) continue;
        const o = imageRect(other);
        const x = Math.min(union.x, o.x), y = Math.min(union.y, o.y);
        union = {
          x, y,
          w: Math.max(union.x + union.w, o.x + o.w) - x,
          h: Math.max(union.y + union.h, o.y + o.h) - y,
        };
      }
      const fileId = im.fileId;
      this.bitmaps.request('img:' + fileId, union, () => loader(fileId));
    }
  }

  private drawStrokePass(ctx: CanvasRenderingContext2D, wr: Rect, pass: 'highlighter' | 'pen') {
    for (const s of this.strokes) {
      if (s.tool !== pass || s.deletedAt || this.excluded.has(s.id)) continue;
      if (!rectsIntersect(s.bbox, wr)) continue;
      ctx.save();
      if (isHighlight(s.color) || s.tool === 'highlighter') ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = resolveInk(s.color, this.theme);
      fillRibbon(ctx, buildRibbon(s.points, s.baseWidth));
      ctx.restore();
    }
  }

  private drawTextPass(ctx: CanvasRenderingContext2D, wr: Rect) {
    for (const box of this.textBoxes) {
      if (box.deletedAt) continue;
      ctx.save();
      ctx.font = textBoxFont(box);
      const rect = textBoxRect(box, (s) => ctx.measureText(s).width);
      ctx.restore();
      this.textBoxRects.set(box.id, rect); // real draw: keep the invalidation cache fresh
      if (!rectsIntersect(rect, wr)) continue;
      drawTextBox(ctx, box, this.theme);
    }
  }

  private makeCanvas(w: number, h: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  }

  destroy() {
    this.cache.clear();
    this.bitmaps.clear();
    this.underlay?.destroy?.(); // release pdfjs document/worker + raster cache
    this.underlay = null;
  }
}

/** Field equality for image invalidation diffing (renderer.setImages). */
function sameImage(a: Image, b: Image): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h
    && a.fileId === b.fileId && a.deletedAt === b.deletedAt;
}

/** Field equality for text-box invalidation diffing (renderer.setTextBoxes). */
function sameTextBox(a: TextBox, b: TextBox): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.text === b.text
    && a.fontSize === b.fontSize && a.bold === b.bold && a.italic === b.italic
    && a.color === b.color && a.deletedAt === b.deletedAt;
}
